import MDBox from "components/MDBox";
import React, { useState, useEffect } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  Stack,
  Box,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import { compnayUsersAction } from "../../../redux/slice/companyUsers";
import AssignUserModal from "./AssignUserModal";
import "../../../styles/kpis.scss";

const SortableLabel = React.memo(
  ({ labelKey, children, teamPayload, handleRequestSort }) => (
    <TableSortLabel
      active={teamPayload?.orderBy === labelKey}
      direction={teamPayload?.orderBy === labelKey ? teamPayload?.order : "asc"}
      onClick={() => handleRequestSort(labelKey)}
    >
      {children}
      {teamPayload?.orderBy === labelKey ? (
        <MDBox component="span" sx={visuallyHidden}>
          {teamPayload?.order === "desc" ? "sorted descending" : "sorted ascending"}
        </MDBox>
      ) : null}
    </TableSortLabel>
  ),
  (prevProps, nextProps) =>
    prevProps.teamPayload === nextProps.teamPayload &&
    prevProps.handleRequestSort === nextProps.handleRequestSort
);

function ListAssignedUsers() {
  const theme = useTheme();
  const isMobileDisplay = useMediaQuery(theme.breakpoints.only("xs"));
  const companyUsers = useSelector((state) => state.companyUsers.companyUsers.list);
  const totalTeamMember = useSelector((state) => state.companyUsers.companyUsers.count);
  const dispatch = useDispatch();
  const { id } = useParams();
  const [teamPayload, setTeamPayload] = useState({
    pageSize: 10,
    pageNumber: 0,
    order: "asc",
    orderBy: "name",
  });
  const [editModalData, setEditModalData] = useState({ isModalOpen: false, user: null, name: "" });

  useEffect(() => {
    dispatch(compnayUsersAction.getCompanyUsersRequest({ ...teamPayload, id }));
  }, [teamPayload]);

  const handlePageChange = (_, newPage) => {
    setTeamPayload((prevPayload) => ({ ...prevPayload, pageNumber: newPage }));
  };

  const handleChangeRowsPerPage = (event) => {
    setTeamPayload((prevPayload) => ({
      ...prevPayload,
      pageSize: parseInt(event.target.value, 10),
    }));
  };

  const handleRequestSort = (property) => {
    const isAsc = teamPayload?.orderBy === property && teamPayload?.order === "asc";
    setTeamPayload((prevPayload) => ({
      ...prevPayload,
      order: isAsc ? "desc" : "asc",
      orderBy: property,
    }));
  };

  return (
    <>
      <TableContainer component={Paper} className="manage-users-datatable">
        <Typography variant="h5" sx={{ px: 3, pt: 3, pb: 1 }}>
          Manage Users
        </Typography>
        <Table aria-label="simple table">
          {!isMobileDisplay && (
            <TableHead sx={{ display: "table-header-group" }}>
              <TableRow>
                <TableCell>
                  <SortableLabel
                    labelKey="name"
                    teamPayload={teamPayload}
                    handleRequestSort={handleRequestSort}
                  >
                    Name
                  </SortableLabel>
                </TableCell>
                <TableCell>
                  <SortableLabel
                    labelKey="teamName"
                    teamPayload={teamPayload}
                    handleRequestSort={handleRequestSort}
                  >
                    Team
                  </SortableLabel>
                </TableCell>
                <TableCell>
                  <SortableLabel
                    labelKey="role"
                    teamPayload={teamPayload}
                    handleRequestSort={handleRequestSort}
                  >
                    Role
                  </SortableLabel>
                </TableCell>
                <TableCell>
                  <SortableLabel
                    labelKey="email"
                    teamPayload={teamPayload}
                    handleRequestSort={handleRequestSort}
                  >
                    Email
                  </SortableLabel>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {[...companyUsers]?.map((row) => (
              <TableRow
                key={row.userId}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() =>
                  !isMobileDisplay &&
                  setEditModalData({ user: row.userId, isModalOpen: true, name: row.name })
                }
              >
                {isMobileDisplay ? (
                  <>
                    <TableCell>
                      <Stack direction="column">
                        <Typography>{row.name}</Typography>
                        <Box className="datatable-box">
                          <Typography className="datatable-mobile-label">Team</Typography>
                          <Typography className="datatable-mobile-value">
                            {row?.teamName}
                          </Typography>
                        </Box>
                        <Box className="datatable-box">
                          <Typography className="datatable-mobile-label">Role</Typography>
                          <Typography className="datatable-mobile-value">{row?.role}</Typography>
                        </Box>
                        <Box className="datatable-box">
                          <Typography className="datatable-mobile-label">Email</Typography>
                          <Typography className="datatable-mobile-value">{row.email}</Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell width="40px">
                      <EditIcon
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setEditModalData({
                            user: row.userId,
                            isModalOpen: true,
                            name: row.name,
                          });
                        }}
                      />
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row?.teamName}</TableCell>
                    <TableCell>{row?.role}</TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>
                      <EditIcon
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setEditModalData({
                            user: row.userId,
                            isModalOpen: true,
                            name: row.name,
                          });
                        }}
                      />
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 25, 50]}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleChangeRowsPerPage}
          component="div"
          count={totalTeamMember}
          rowsPerPage={teamPayload?.pageSize}
          page={teamPayload.pageNumber}
          sx={{ flex: "1 1 auto" }}
        />
      </TableContainer>
      {editModalData.isModalOpen && (
        <AssignUserModal
          editModalData={editModalData}
          open={editModalData.isModalOpen}
          payload={{ ...teamPayload, id }}
          handleClose={() => setEditModalData({ user: null, isModalOpen: false, name: "" })}
        />
      )}
    </>
  );
}

export default ListAssignedUsers;
